import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import ContactForm from '../components/ContactForm';
import GallerySection from '../components/GallerySection';
import { getCdnImage } from '../lib/imageUtils';

export default function ServicePaving() {
  const serviceItems = [
    {
      title: 'Lægning af alle typer belægningssten',
      desc: 'Vi lægger herregårdssten, modulfliser, klinke- og kantsten til indkørsler, terrasser, gangstier og erhvervsarealer med flotte, rene linjer.',
      icon: 'fa-solid fa-shapes',
    },
    {
      title: 'Professionel bundopbygning og afretning',
      desc: 'Et solidt resultat kræver korrekt udgravning til bæredygtig bund, udlægning af fiberdug og omhyggelig laserstyret afretning.',
      icon: 'fa-solid fa-ruler-combined',
    },
    {
      title: 'Stabilt underlag der mindsker risikoen for sætninger',
      desc: 'Vi anvender de rette komprimeringsteknikker og lagtykkelser, så du undgår køre- og hjulspor samt ujævnheder over tid.',
      icon: 'fa-solid fa-shield',
    },
    {
      title: 'Levering og kørsel af materialer',
      desc: 'Vi leverer og udlægger stabilgrus, afretningssand/flisesand, nøddesten, ærtesten og granitskærver med egne vogne og maskiner.',
      icon: 'fa-solid fa-truck-ramp-box',
    },
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <SeoMeta
        title="Belægning & anlæg - ENTREPRENØR LANG ApS"
        description="Holdbare og professionelle belægningsløsninger til indkørsler, terrasser og erhvervsarealer. Bundopbygning, afretning og flisearbejde."
        keywords="belægning, anlæg, herregårdssten, fliser, indkørsel, terrasse, stabilgrus, afretning, Lolland, Falster, Øster Ulslev"
      />

      {/* Hero */}
      <section className="relative bg-slate-950 text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/belaegning-herregaardssten-indkoersel.jpg', {
              width: 1920,
              quality: 80,
            })}
            alt="Belægning herregårdssten indkørsel"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider"
            >
              <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
              <span>Tilbage til alle ydelser</span>
            </Link>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Belægning & anlæg
            </h1>
            <p className="text-lg text-slate-200 leading-relaxed font-medium">
              Holdbare og professionelle belægningsløsninger til indkørsler, terrasser og erhvervsarealer.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="#kontakt"
                className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow transition-all"
              >
                Få et tilbud
              </a>
              <a
                href="tel:40309708"
                className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-white/20 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone mr-2 text-emerald-400" aria-hidden="true" />
                Ring 40 30 97 08
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-2">
                <i className="fa-solid fa-layer-group text-xs" aria-hidden="true" />
                Kvalitet fra bunden
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Grundig bundopbygning er hemmeligheden bag et langtidsholdbart anlæg
              </h2>
              <p className="text-slate-600 text-base mt-3 leading-relaxed">
                En flot flisebelægning eller indkørsel er kun så god som det underlag, den hviler på. Hos ENTREPRENØR LANG ApS sparer vi aldrig på bundopbygningen. Vi sørger for korrekt dybde, bærelag af stabilgrus, velafrettet sand og ordentlig kantafgrænsning, så stenene bliver liggende stabilt år efter år uden sætningsskader eller vandpytter.
              </p>
            </div>

            {/* Service Point Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {serviceItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-2"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center">
                    <i className={`${item.icon} text-base`} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-base leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visuals - real leveling photo */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-white">
              <img
                src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-481666921-1140782317749396-168441956002480996-n.jpg', {
                  width: 800,
                  quality: 85,
                })}
                alt="Maskinel brolægning af belægningssten med hydraulisk stenklemme på gravemaskine"
                className="w-full h-80 object-cover"
              />
              <div className="p-5">
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                  Projektfoto fra entreprenørarbejde
                </span>
                <p className="text-slate-900 font-bold text-base mt-1">
                  Præcis planering og afretning
                </p>
                <p className="text-slate-600 text-xs sm:text-sm mt-1">
                  Laserstyret afretning for et helt jævnt fundament klar til belægning eller græssåning.
                </p>
              </div>
            </div>

            <div className="bg-slate-900 text-white p-6 rounded-2xl space-y-3">
              <h4 className="font-bold text-base flex items-center gap-2">
                <i className="fa-solid fa-truck-pickup text-emerald-400" aria-hidden="true" />
                Levering af grus og sand
              </h4>
              <p className="text-slate-300 text-xs sm:text-sm">
                Skal du have leveret materialer til dit eget haveprojekt? Vi kører stabilgrus, flisesand og pyntesten ud på hele Lolland-Falster.
              </p>
              <a
                href="tel:40309708"
                className="inline-flex items-center gap-2 text-xs font-bold text-emerald-400 hover:text-emerald-300 pt-1 whitespace-nowrap"
              >
                <span>Hør om priser og levering: 40 30 97 08</span>
              </a>
            </div>
          </div>
        </div>

        {/* Dynamic CMS Gallery Section */}
        <GallerySection
          gallerySlug="belaegning"
          title="Billeder fra belægnings- og anlægsarbejde"
          subtitle="Se billeder af udgravning, bundopbygning og afretning fra vores projekter."
        />
      </section>

      {/* Form */}
      <section id="kontakt" className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactForm defaultService="Belægning & anlæg" />
        </div>
      </section>
    </div>
  );
}
